import React, { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, errorInfo: null };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Error caught by boundary:', error, errorInfo);
    this.setState({ errorInfo });
    
    // Log the component stack
    if (errorInfo && errorInfo.componentStack) {
      console.error('Component stack:', errorInfo.componentStack);
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 p-4">
          <div className="max-w-md w-full bg-white dark:bg-gray-800 shadow-lg rounded-lg p-6">
            <h1 className="text-xl font-bold text-red-600 mb-4">Something went wrong!</h1>
            <div className="bg-gray-100 dark:bg-gray-700 rounded p-4 mb-4">
              <p className="text-sm font-mono text-gray-700 dark:text-gray-300">
                {this.state.error?.toString()}
              </p>
            </div>
            {this.state.error?.message === 'updatePatientInfo is not defined' && (
              <div className="bg-yellow-100 dark:bg-yellow-900 border border-yellow-400 dark:border-yellow-600 rounded p-4 mb-4">
                <p className="text-sm text-yellow-700 dark:text-yellow-300">
                  This error is likely due to cached browser data. Please:
                  <ol className="list-decimal list-inside mt-2">
                    <li>Clear your browser cache (Ctrl+Shift+Delete)</li>
                    <li>Hard refresh the page (Ctrl+F5)</li>
                    <li>If the error persists, restart the development server</li>
                  </ol>
                </p>
              </div>
            )}
            <button
              onClick={() => window.location.reload()}
              className="w-full bg-primary text-white py-2 px-4 rounded hover:bg-primary-dark transition-colors"
            >
              Reload Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;